import React from "react";
import { CheckCircle, Circle, CaretDown } from "@phosphor-icons/react";
import TaskDetailsPanel from "@/components/TaskDetailsPanel";

// Reusable checklist-item row shared between "Organizza la partenza" (via
// ChecklistSection's inline usage of its parts) and "A bordo · Scuola USA
// operativa". Visual pattern is intentionally identical to the classic
// Organizza item: `font-sans font-semibold` title, duotone check icons,
// `≈ SCADENZA` uppercase hint, terracotta "Approfondisci ▾" toggle, and a
// tri-color Perché/Cosa fare/Attenzione details panel.
//
// Props:
//   title           string  — required
//   deadline_hint   string  — optional, rendered uppercase after "≈"
//   why/how/careful string  — content of the expanded details panel
//   done            bool    — checkbox state
//   expanded        bool    — whether the details panel is visible
//   onToggle        fn      — flips `done` (checkbox click)
//   onToggleExpand  fn      — flips `expanded` (Approfondisci button)
//   labels          {learnMore, showLess, why, how, careful}  — i18n strings
//   testId          string  — root data-testid for the card
export default function TaskCard({
  title,
  deadline_hint,
  why, how, careful,
  done,
  expanded,
  onToggle,
  onToggleExpand,
  labels = {},
  testId,
}) {
  const {
    learnMore = "Approfondisci",
    showLess = "Nascondi",
    why: whyLabel = "Perché",
    how: howLabel = "Cosa fare",
    careful: carefulLabel = "Attenzione",
  } = labels;
  const hasDetails = !!(why || how || careful);
  return (
    <div
      data-testid={testId}
      data-done={done ? "true" : "false"}
      className="relative rounded-2xl bg-surface-card border border-border overflow-hidden"
    >
      <div className="flex items-start gap-3 p-4">
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={done}
          className="mt-0.5 shrink-0"
          aria-label={done ? "Segna come da fare" : "Segna come fatto"}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {done
            ? <CheckCircle size={26} weight="duotone" className="text-sage" />
            : <Circle size={26} weight="regular" className="text-ink-soft/50" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <div className={`font-sans font-semibold text-ink ${done ? "opacity-60" : ""}`}>
              {title}
            </div>
          </div>
          {deadline_hint && (
            <p
              className="text-[10px] uppercase tracking-wider font-sans text-ink-soft/70 mt-2"
              data-testid={testId ? `${testId}-deadline-hint` : undefined}
            >
              ≈ {deadline_hint}
            </p>
          )}
          {hasDetails && (
            <button
              type="button"
              onClick={onToggleExpand}
              aria-expanded={expanded}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-sans font-semibold text-terracotta hover:text-terracotta-hover transition-colors"
              data-testid={testId ? `${testId}-expand` : undefined}
            >
              <CaretDown
                size={14}
                weight="bold"
                className={`transition-transform duration-300 ${expanded ? "rotate-180" : "rotate-0"}`}
              />
              {expanded ? showLess : learnMore}
            </button>
          )}
        </div>
      </div>
      {expanded && hasDetails && (
        <div
          data-testid={testId ? `${testId}-details` : undefined}
          className="px-4 pb-5 pt-3 border-t border-border/40 mx-4"
        >
          <TaskDetailsPanel
            why={why}
            how={how}
            careful={careful}
            whyLabel={whyLabel}
            howLabel={howLabel}
            carefulLabel={carefulLabel}
          />
        </div>
      )}
    </div>
  );
}
